import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Truck,
  Clock3,
  PackageCheck,
  ShieldCheck,
  Globe2,
  AlertTriangle,
  Wallet,
  FileText,
  Mail,
  HelpCircle,
  MapPin,
  Split,
  CloudLightning,
  ScrollText,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useShippingConfig, formatEuro, headlineFreeOver } from "@/hooks/useShippingConfig";
import { FREE_SHIPPING_OVER_CENTS_FALLBACK } from "@/lib/site-config";

const SITE_URL = "https://fixhen.com";
const PAGE_URL = `${SITE_URL}/shipping`;
const CONTACT_EMAIL = "info@contact.fixhen.com";
const DISPATCH_BUSINESS_DAYS = 2;
const CUTOFF_LOCAL = "17:00 EET";
const CLAIM_WINDOW_HOURS = 48;

// FAQ — used both for JSON-LD and the visible accordion below.
const FAQS: { q: string; a: string }[] = [
  {
    q: "When exactly does the 2-business-day dispatch clock start?",
    a: `The clock starts once your payment is confirmed as received by FIXHEN, provided that confirmation lands before ${CUTOFF_LOCAL} on a Bulgarian business day. Orders confirmed after cut-off, on weekends, or on Bulgarian public holidays begin counting from the start of the next business day. Bank transfers count as received on the day the funds credit our account — not the day you initiate the transfer.`,
  },
  {
    q: "What counts as a business day?",
    a: "Monday through Friday, excluding official public holidays observed in the Republic of Bulgaria, as published by the Council of Ministers.",
  },
  {
    q: "Are all delivery time estimates guaranteed?",
    a: "No. Transit times are indicative windows provided by the carrier and are not part of our contractual obligations. FIXHEN's obligation is limited to handing the parcel to the carrier within the dispatch window; the actual delivery date depends on the carrier, customs, and any force majeure events beyond our reasonable control.",
  },
  {
    q: "Can I change the delivery address after the order is dispatched?",
    a: "Once the parcel has been handed to the carrier and a tracking number has been issued, address changes may not be possible. Where the carrier permits an in-transit reroute, any additional fees imposed by the carrier are borne by the customer. Requests must be submitted in writing to our support address before the parcel is out for delivery.",
  },
  {
    q: "What happens if the carrier loses my parcel?",
    a: "If tracking has not moved for 7 consecutive business days for domestic shipments or 15 business days for international shipments, contact us in writing and we will open an investigation with the carrier. If the carrier officially declares the parcel lost, we will, at our discretion, resend the items or issue a full refund of the goods and original shipping cost after the carrier's declaration is received.",
  },
  {
    q: "Do you insure shipments?",
    a: "All shipments include the standard carrier liability applicable to the chosen service. Extended, declared-value insurance is available on request against an additional fee; it must be requested and paid for before dispatch. Uninsured shipments travel at the customer's risk beyond the statutory rules on risk transfer set out in this policy.",
  },
  {
    q: "Why is my tracking not updating?",
    a: "Carriers typically create the label first and only scan the parcel in at the first sorting hub, which can take up to 24 hours from dispatch. International parcels may also show gaps of 24-72 hours while in transit between hubs or in customs. If no scan appears within 3 business days of the dispatch email, please contact us.",
  },
  {
    q: "What if I refuse the parcel or provide the wrong address?",
    a: "A refused, undeliverable, or wrongly-addressed parcel that returns to us is treated as a voluntary withdrawal by the customer. We refund the value of the goods after inspection but retain the actual outbound and return shipping costs, plus any storage or handling fees charged to us by the carrier. Because we ship Delivered Duty Paid, no customs duty is charged to you.",
  },
];

// Transit windows and shipping rates are rendered live from the shipping_zones
// table via useShippingConfig() — the same rate table the checkout calculates
// with — so this page always reflects the admin configuration.

export const Route = createFileRoute("/shipping")({
  head: () => {
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "FIXHEN Shipping Policy",
        url: PAGE_URL,
        description:
          "FIXHEN dispatches paid orders within 2 business days. Read the full shipping policy: transit times, tracking, customs, risk transfer, damaged shipments and liability.",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Shipping", item: PAGE_URL },
        ],
      },
    ];
    return {
      meta: [
        { title: "Shipping Policy — 2 Business-Day Dispatch | FIXHEN" },
        {
          name: "description",
          content: `FIXHEN dispatches paid orders within ${DISPATCH_BUSINESS_DAYS} business days. Live transit times and shipping rates, tracking, Delivered Duty Paid customs (duties included in the price), risk transfer, damaged-shipment procedure and liability limits — for consumers and businesses alike.`,
        },
        { property: "og:title", content: "Shipping Policy — 2 Business-Day Dispatch | FIXHEN" },
        {
          property: "og:description",
          content: `Dispatch within ${DISPATCH_BUSINESS_DAYS} business days of confirmed payment. Worldwide delivery with clear, EU-compliant terms — customs duties already paid, no surprise charges.`,
        },
        { property: "og:type", content: "article" },
        { property: "og:url", content: PAGE_URL },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Shipping Policy | FIXHEN" },
        {
          name: "twitter:description",
          content: `Dispatch within ${DISPATCH_BUSINESS_DAYS} business days of confirmed payment. Worldwide delivery.`,
        },
        { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
        { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },
      ],
      links: [{ rel: "canonical", href: PAGE_URL }],
      scripts: jsonLd.map((data) => ({
        type: "application/ld+json",
        children: JSON.stringify(data),
      })),
    };
  },
  component: ShippingPage,
});

function ShippingPage() {
  const lastUpdated = "15 July 2026";
  const { data } = useShippingConfig();
  const freeOver = headlineFreeOver(data?.zones) ?? FREE_SHIPPING_OVER_CENTS_FALLBACK;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:py-16">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li className="text-foreground">Shipping</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="mb-10 border-b border-border pb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            <Truck className="h-3.5 w-3.5" /> Legal · Shipping & Delivery
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-foreground sm:text-5xl">
            Shipping Policy
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            How FIXHEN dispatches, ships and delivers parts worldwide. Placing an order
            constitutes acceptance of this policy, which forms part of our{" "}
            <Link to="/terms" className="font-semibold text-foreground underline decoration-primary/40 underline-offset-2 hover:decoration-primary">
              Terms of Sale
            </Link>
            .
          </p>
          <p className="mt-4 text-xs text-muted-foreground">Last updated: {lastUpdated}</p>
        </header>

        {/* Summary cards */}
        <section className="mb-10 grid gap-3 sm:grid-cols-3">
          <SummaryCard
            icon={<Clock3 className="h-5 w-5" />}
            title={`Within ${DISPATCH_BUSINESS_DAYS} business days`}
            body="Dispatch window from confirmed payment, subject to stock and cut-off."
          />
          <SummaryCard
            icon={<Globe2 className="h-5 w-5" />}
            title="Worldwide"
            body="EU, UK, EFTA and international with tracked carrier service."
          />
          <SummaryCard
            icon={<Wallet className="h-5 w-5" />}
            title={`Free over ${formatEuro(freeOver)}`}
            body="Automatically applied at checkout on eligible destinations."
          />
        </section>

        {/* Body */}
        <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-black prose-headings:tracking-tight prose-h2:mt-12 prose-h2:text-2xl prose-h3:text-lg prose-a:text-primary prose-strong:text-foreground">
          <h2 id="scope">
            <ScrollText className="mr-2 inline h-5 w-5" />
            1. Scope & legal basis
          </h2>
          <p>
            This Shipping Policy (the "Policy") governs the fulfilment and dispatch of goods
            purchased through <strong>fixhen.com</strong> (the "Site"), operated by FIXHEN
            (the "Seller", "we", "our"). It applies to all orders — consumer (B2C) and
            business (B2B) — and takes precedence over any oral undertakings, marketing
            copy, or informal statements that may suggest different timelines. For EU
            consumers, this Policy operates alongside the mandatory provisions of
            Directive 2011/83/EU on Consumer Rights and Directive (EU) 2019/771 on the
            sale of goods, and does not limit any statutory right that cannot lawfully be
            excluded.
          </p>

          <h2 id="dispatch">
            <PackageCheck className="mr-2 inline h-5 w-5" />
            2. Dispatch timeline — {DISPATCH_BUSINESS_DAYS} business days
          </h2>
          <p>
            FIXHEN targets dispatch of every in-stock order within{" "}
            <strong>{DISPATCH_BUSINESS_DAYS} (two) business days</strong> starting from the
            first business day on which payment is <em>confirmed as received</em>. Dispatch
            is fulfilled when the parcel is handed to the carrier and a tracking number is
            issued.
          </p>
          <ul>
            <li>
              <strong>Cut-off.</strong> Orders confirmed after <strong>{CUTOFF_LOCAL}</strong> on a
              business day, on weekends, or on Bulgarian public holidays are treated as
              received on the next business day for the purposes of the dispatch clock.
            </li>
            <li>
              <strong>Payment methods.</strong> Card and instant-settlement payments are
              treated as received upon successful authorisation. Bank transfers count only
              from the day funds credit our account; the customer bears any delay caused
              by intermediary banks, SEPA processing windows, or incomplete payment
              references.
            </li>
            <li>
              <strong>Backorder / made-to-order items.</strong> Products flagged as "on
              backorder", "pre-order", or "made to order" are dispatched within the lead
              time stated on the product page and are expressly excluded from the{" "}
              {DISPATCH_BUSINESS_DAYS}-business-day dispatch window.
            </li>
            <li>
              <strong>Address verification.</strong> If we identify a suspected mismatch
              between the billing and shipping data, or the order is flagged for anti-fraud
              review, the dispatch clock is paused until the review is closed. We will
              contact the customer at the email address supplied at checkout.
            </li>
          </ul>

          <h2 id="transit">
            <Globe2 className="mr-2 inline h-5 w-5" />
            3. Delivery times & shipping rates (transit)
          </h2>
          <p>
            Once the parcel is with the carrier, indicative transit times and the
            shipping rates applied at checkout are shown below. These figures are read
            live from the same rate table the checkout uses, so what you see here is what
            you pay. Transit times are estimates supplied by carriers, not contractual
            guarantees, and are counted in business days from the day of first carrier
            scan:
          </p>
          <div className="not-prose overflow-x-auto overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-semibold">Destination</th>
                  <th className="px-4 py-2 font-semibold">Estimated transit</th>
                  <th className="px-4 py-2 font-semibold">Shipping fee</th>
                  <th className="px-4 py-2 font-semibold">Free over</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.zones ?? []).map((z) => (
                  <tr key={z.code} className="bg-card">
                    <td className="px-4 py-3 font-medium text-foreground">{z.name_en}</td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">
                      {z.eta_min_days}–{z.eta_max_days} business days
                    </td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">
                      {formatEuro(z.price_cents)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">
                      {z.free_over_cents != null ? formatEuro(z.free_over_cents) : "—"}
                    </td>
                  </tr>
                ))}
                {(!data?.zones || data.zones.length === 0) && (
                  <tr className="bg-card">
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      Loading live rates…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="!mt-4 text-xs text-muted-foreground">
            The rates and transit windows above are pulled in real time from the FIXHEN
            shipping configuration — the same data the checkout calculates with. If a zone
            is added or a price changes in the admin panel, this table updates
            automatically, so it is always in sync with what customers pay.
          </p>
          <p className="!mt-6">
            FIXHEN does not accept liability for delays caused by the carrier, customs
            clearance, incorrect or incomplete addresses, refusal to accept the parcel,
            weather, strikes, labour actions, network outages, or any other event of{" "}
            <em>force majeure</em>. Time is not of the essence for delivery unless we have
            agreed a specific delivery date in writing and confirmed the additional fee
            for a time-critical service.
          </p>

          <h2 id="costs">
            <Wallet className="mr-2 inline h-5 w-5" />
            4. Shipping costs & free-shipping threshold
          </h2>
          <p>
            Shipping fees are calculated automatically at checkout based on destination,
            weight, and dimensional volume. The final total shown before payment is the
            definitive amount; no additional shipping surcharges are levied by FIXHEN
            after checkout. Where the eligible order subtotal exceeds{" "}
            <strong>{formatEuro(freeOver)}</strong>, the standard shipping fee is waived on
            supported destinations. The threshold applies to the goods subtotal only and
            excludes taxes, duties, and any express-service upgrades.
          </p>

          <h2 id="tracking">
            <MapPin className="mr-2 inline h-5 w-5" />
            5. Order tracking
          </h2>
          <p>
            A dispatch confirmation email with a tracking number is sent as soon as the
            carrier collects the parcel. Tracking activation with the carrier typically
            takes up to 24 hours; where the tracking number returns no scans within{" "}
            <strong>3 business days</strong> of the dispatch email, please contact us in
            writing so we can open a carrier investigation. Registered customers can also
            view order status from their account area.
          </p>

          <h2 id="customs">
            <ShieldCheck className="mr-2 inline h-5 w-5" />
            6. Customs, duties & import taxes (non-EU)
          </h2>
          <p>
            For shipments to destinations outside the European Union customs territory,
            FIXHEN ships on a <strong>Delivered Duty Paid (DDP)</strong> basis. This means{" "}
            <strong>we are responsible for all import duties, VAT, brokerage and clearance
            fees</strong>, and the duty on the goods is already paid before the parcel
            leaves our warehouse. The price you see at checkout is the final price — there
            are no surprise customs charges on delivery, and the carrier will not collect
            any fees from you at the door.
          </p>
          <ul>
            <li>
              <strong>No additional charges on delivery.</strong> All customs duties and
              import taxes are included in the order total; you will never be asked to pay
              anything extra to release the parcel.
            </li>
            <li>
              <strong>Accurate declarations.</strong> FIXHEN declares the true commercial
              value of the goods on all customs paperwork and does not mark commercial
              shipments as "gift".
            </li>
            <li>
              <strong>Returns.</strong> If a parcel is refused or returned to us for
              reasons other than customs charges, the actual outbound and return shipping
              costs, plus any storage or handling fees charged by the carrier, are
              deducted from the refund — but no customs duty is ever charged to you.
            </li>
          </ul>

          <h2 id="address">
            <MapPin className="mr-2 inline h-5 w-5" />
            7. Address accuracy & failed delivery
          </h2>
          <p>
            The customer warrants that the delivery address, recipient name, and contact
            details submitted at checkout are accurate and complete. FIXHEN is not
            responsible for non-delivery, delay, loss, or additional cost arising from an
            inaccurate, incomplete, or outdated address, or from the recipient's failure
            to be available to receive the parcel or to collect it from the carrier's
            depot within the carrier's holding period. Where a parcel is returned to us
            for these reasons, the customer bears the actual outbound and return shipping
            cost, and any storage or handling fees charged by the carrier, which we may
            set off against the refund of the goods.
          </p>

          <h2 id="risk">
            <ShieldCheck className="mr-2 inline h-5 w-5" />
            8. Risk of loss, title, and reservation of ownership
          </h2>
          <p>
            <strong>Consumers (B2C).</strong> Risk of loss or damage to the goods passes
            to the consumer when the consumer, or a third party designated by the consumer
            (other than the carrier), takes physical possession of the goods, in line with
            Article 20 of Directive 2011/83/EU.
          </p>
          <p>
            <strong>Business customers (B2B).</strong> Risk passes to the buyer upon
            handover of the goods to the first carrier. Title in the goods remains with
            FIXHEN until the full purchase price, including shipping and any applicable
            taxes and fees, is received in cleared funds (<em>retention of title</em>).
            Until then, the buyer holds the goods as bailee, keeps them separated and
            identifiable, and insures them at their full replacement value.
          </p>

          <h2 id="damage">
            <AlertTriangle className="mr-2 inline h-5 w-5" />
            9. Damaged or short-shipped parcels
          </h2>
          <p>
            The customer must inspect the parcel on receipt. Any visible external damage,
            missing items, or evidence of tampering must be noted with the carrier at the
            point of handover where possible, and reported to FIXHEN in writing at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within{" "}
            <strong>{CLAIM_WINDOW_HOURS} hours</strong> of delivery, together with
            photographs of:
          </p>
          <ul>
            <li>the outer packaging as delivered, including all carrier labels;</li>
            <li>the inner packaging and packing materials as delivered;</li>
            <li>each affected item, showing the damage or defect clearly.</li>
          </ul>
          <p>
            Claims raised after the {CLAIM_WINDOW_HOURS}-hour window, or without the
            evidence above, may be rejected because we lose our ability to raise a
            successful claim against the carrier. Where a claim is accepted, FIXHEN will,
            at its option and at no additional cost to the customer, replace the goods,
            ship the missing items, or issue a refund for the affected portion of the
            order including the corresponding shipping cost.
          </p>

          <h2 id="split">
            <Split className="mr-2 inline h-5 w-5" />
            10. Partial and split shipments
          </h2>
          <p>
            FIXHEN reserves the right to split a single order into multiple shipments —
            for example when items ship from different warehouses or when one item is
            temporarily on backorder — at no additional shipping cost to the customer.
            Each parcel is dispatched with its own tracking number and its own dispatch
            confirmation email. Where an order is split at the customer's request after
            checkout, any additional shipping cost is borne by the customer.
          </p>

          <h2 id="force-majeure">
            <CloudLightning className="mr-2 inline h-5 w-5" />
            11. Force majeure
          </h2>
          <p>
            The dispatch and delivery timelines described above are suspended for the
            duration of any event beyond FIXHEN's reasonable control, including without
            limitation acts of God, natural disasters, epidemics, war, terrorism, civil
            disturbance, labour disputes, carrier network outages, customs actions,
            embargoes, government orders, cyber-attacks, and utility failures. FIXHEN
            will make reasonable efforts to notify affected customers and to resume
            normal operations without undue delay.
          </p>

          <h2 id="liability">
            <ShieldCheck className="mr-2 inline h-5 w-5" />
            12. Limitation of liability
          </h2>
          <p>
            Nothing in this Policy excludes or limits FIXHEN's liability for death or
            personal injury caused by negligence, for fraud, or for any other liability
            that cannot be lawfully excluded. Subject to the foregoing, and to the fullest
            extent permitted by applicable law:
          </p>
          <ul>
            <li>
              FIXHEN's aggregate liability arising out of or in connection with the
              shipment of an order is limited to the total amount actually paid by the
              customer for that order, including shipping;
            </li>
            <li>
              FIXHEN is not liable for indirect, consequential, or special damages,
              including without limitation loss of profit, loss of use, vehicle downtime,
              loss of a scheduled workshop slot, rental-car costs, or missed opportunities;
            </li>
            <li>
              statutory rights of consumers are not affected and continue to apply in full.
            </li>
          </ul>

          <h2 id="returns-link">
            <FileText className="mr-2 inline h-5 w-5" />
            13. Relation to Returns Policy
          </h2>
          <p>
            This Policy governs outbound shipping only. Withdrawal from the contract,
            return of goods, and refund processing are governed by our separate{" "}
            <Link to="/returns" className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary">
              Returns Policy
            </Link>
            . Where the two documents overlap on the mechanics of return shipping, the
            Returns Policy prevails.
          </p>

          <h2 id="law">
            <ScrollText className="mr-2 inline h-5 w-5" />
            14. Governing law & jurisdiction
          </h2>
          <p>
            This Policy is governed by the laws of the Republic of Bulgaria, without
            prejudice to the mandatory consumer-protection provisions of the country of
            the consumer's habitual residence within the EU. Any dispute arising in
            connection with the shipment of goods is subject to the exclusive
            jurisdiction of the competent courts of Sofia, Bulgaria; consumers retain the
            right to bring proceedings before the competent courts of their habitual
            residence and to use the European Commission's Online Dispute Resolution
            platform at{" "}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">
              ec.europa.eu/consumers/odr
            </a>
            .
          </p>

          <h2 id="faq">
            <HelpCircle className="mr-2 inline h-5 w-5" />
            15. Frequently asked questions
          </h2>
          <div className="not-prose space-y-3">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-lg border border-border bg-card p-4 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground marker:hidden">
                  <span className="mr-2 text-primary group-open:hidden">+</span>
                  <span className="mr-2 hidden text-primary group-open:inline">−</span>
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>

          <h2 id="contact">
            <Mail className="mr-2 inline h-5 w-5" />
            16. Contact
          </h2>
          <p>
            Shipping enquiries — <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            Response time: one business day, Monday to Friday, excluding Bulgarian public
            holidays. Please include your order number in the subject line so we can
            respond faster.
          </p>
        </article>

        {/* CTA */}
        <aside className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-6">
          <h3 className="text-lg font-bold text-foreground">Question about a specific parcel?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Email{" "}
            <a
              className="font-medium text-primary hover:underline"
              href={`mailto:${CONTACT_EMAIL}?subject=Shipping%20enquiry`}
            >
              {CONTACT_EMAIL}
            </a>{" "}
            with your order number and, if relevant, the tracking number. We reply within
            one business day.
          </p>
        </aside>
      </main>
      <SiteFooter />
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-primary">
        {icon}
        <span className="text-sm font-bold text-foreground">{title}</span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
